import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const pillars = [
  {
    number: '01',
    title: 'Architecture',
    description: 'Designing systems that scale. Microservices, clean architecture, and domain-driven design patterns that keep large codebases maintainable over years, not months.',
    accent: '#00f2ff',
    icon: '⬡',
  },
  {
    number: '02',
    title: 'Performance',
    description: 'Optimizing real-world user experiences. From React render cycles to SQL query plans — performance is a feature, not an afterthought.',
    accent: '#a78bfa',
    icon: '◈',
  },
  {
    number: '03',
    title: 'Cloud',
    description: 'Deploying and operating modern applications at scale. Azure, Docker, CI/CD pipelines, and infrastructure as code that ships reliably.',
    accent: '#60a5fa',
    icon: '◎',
  },
  {
    number: '04',
    title: 'AI',
    description: 'Integrating intelligent capabilities into products. LLM APIs, AI agents, and practical machine learning that adds real value to applications.',
    accent: '#f0abfc',
    icon: '◇',
  },
];

export default function Engineering() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="approach" className="section-padding overflow-hidden" style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(0,242,255,0.1), transparent)',
      }} />

      {/* Background gradient accent */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(13, 20, 29, 0.8) 0%, transparent 70%)',
      }} />

      <div className="container-custom" ref={ref}>
        {/* Header */}
        <div style={{ marginBottom: '5rem', maxWidth: '640px' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="section-label mb-4"
          >
            03 / APPROACH
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="heading-primary mb-5"
          >
            Engineering{' '}
            <span className="text-gradient">beyond code.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ color: '#849495', lineHeight: 1.7, fontSize: '1.05rem' }}
          >
            Software engineering is more than writing functions. It's about understanding
            systems, users, and constraints — and making decisions that hold up over time.
          </motion.p>
        </div>

        {/* Pillar grid — 2 × 2 on desktop */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}>
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.number}
              initial={{ opacity: 0, y: 32 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'relative',
                padding: '2.5rem',
                background: 'rgba(13,20,29,0.7)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
                backdropFilter: 'blur(12px)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${pillar.accent}25`;
                e.currentTarget.style.background = 'rgba(25,32,41,0.8)';
                e.currentTarget.style.boxShadow = `0 24px 60px rgba(0,0,0,0.4), 0 0 30px ${pillar.accent}08`;
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.background = 'rgba(13,20,29,0.7)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Large background number */}
              <div style={{
                position: 'absolute', top: '1rem', right: '1.5rem',
                fontFamily: 'Sora, sans-serif',
                fontSize: '5rem',
                fontWeight: 800,
                color: `${pillar.accent}06`,
                lineHeight: 1,
                userSelect: 'none',
                pointerEvents: 'none',
              }}>
                {pillar.number}
              </div>

              {/* Top accent line */}
              <div style={{
                position: 'absolute', top: 0, left: '2.5rem', right: '2.5rem', height: '2px',
                background: `linear-gradient(90deg, transparent, ${pillar.accent}50, transparent)`,
              }} />

              {/* Icon symbol */}
              <div style={{
                fontFamily: 'monospace',
                fontSize: '1.5rem',
                color: pillar.accent,
                marginBottom: '1.25rem',
                textShadow: `0 0 20px ${pillar.accent}60`,
              }}>
                {pillar.icon}
              </div>

              {/* Small number label */}
              <div className="text-mono" style={{
                color: pillar.accent,
                fontSize: '0.65rem',
                letterSpacing: '0.1em',
                marginBottom: '0.75rem',
                opacity: 0.7,
              }}>
                {pillar.number} / 04
              </div>

              {/* Title */}
              <h3 style={{
                fontFamily: 'Sora, sans-serif',
                fontSize: '1.4rem',
                fontWeight: 700,
                color: '#dce3f0',
                letterSpacing: '-0.02em',
                marginBottom: '0.75rem',
              }}>
                {pillar.title}
              </h3>

              {/* Description */}
              <p style={{
                color: '#849495',
                fontSize: '0.9rem',
                lineHeight: 1.7,
              }}>
                {pillar.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
      }} />
    </section>
  );
}
